import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import {
  deleteCandidate,
  getVacancies,
  listCandidates,
  moveCandidateStage,
  PIPELINE_STAGES,
  PipelineStage,
} from '../../../../network/ats';
import { useToast } from '../../../../utils/useToast';
import { OfferVacancyDialog } from '../Dialogs/OfferVacancyDialog';
import {
  CandidateFormDialog,
  PipelineCandidate,
} from './CandidateFormDialog';
import { RejectCandidateDialog } from './RejectCandidateDialog';
import {
  Modal,
  errorMessage,
  fieldClass,
  labelClass,
  primaryButtonClass,
  secondaryButtonClass,
} from './Modal';

export const PIPELINE_PAGE_SIZE = 20;

const STAGE_LABEL = Object.fromEntries(
  PIPELINE_STAGES.map((stage) => [stage.value, stage.label]),
) as Record<string, string>;

const STAGE_ALIASES: Record<string, PipelineStage> = {
  new: 'applied',
  responded: 'applied',
  hrscreening: 'screening',
  technicaltest: 'screening',
  interviewing: 'interview',
  offered: 'offer',
};

/** The pipeline stage for a stored status (older records use other spellings). */
export function stageOf(status: unknown): PipelineStage {
  const key = String(status ?? '').trim().toLowerCase();
  if (STAGE_LABEL[key]) return key as PipelineStage;
  return STAGE_ALIASES[key] ?? 'applied';
}

export function toPipelineCandidate(raw: any): PipelineCandidate {
  const details = raw?.details ?? {};
  return {
    id: String(raw?.system?.id ?? details.id ?? ''),
    name: details.name ?? '',
    email: details.email ?? '',
    phone: details.phone ?? '',
    position: details.position ?? '',
    location: details.location ?? '',
    description: details.description ?? '',
    jobId: details.jobId ? String(details.jobId) : '',
    stage: stageOf(details.status),
    dateCreated: raw?.audit?.dateCreated ?? details.date,
    resumeUrl: details.resume?.url || undefined,
  };
}

/**
 * The recruitment pipeline: candidates for a vacancy (or all), filtered by
 * stage, with stage moves, rejection, editing, deletion and adding.
 */
export const Pipeline = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [vacancyId, setVacancyId] = useState('');
  const [stage, setStage] = useState<PipelineStage | ''>('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const [formCandidate, setFormCandidate] = useState<
    PipelineCandidate | null | undefined
  >(undefined); // undefined: closed, null: add, candidate: edit
  const [rejecting, setRejecting] = useState<PipelineCandidate | null>(null);
  const [deleting, setDeleting] = useState<PipelineCandidate | null>(null);
  const [emailing, setEmailing] = useState<PipelineCandidate | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [movingId, setMovingId] = useState<string | null>(null);

  // Search after typing pauses, not on every key.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => setPage(1), [vacancyId, stage, search]);

  const { data: vacancies = [] } = useQuery({
    queryKey: ['vacancies'],
    queryFn: getVacancies,
  });

  const params = { vacancyId, stage, search, page, limit: PIPELINE_PAGE_SIZE };
  const {
    data: result,
    isLoading,
    isError,
    isFetching,
  } = useQuery({
    queryKey: ['pipeline', params],
    queryFn: () => listCandidates(params),
    keepPreviousData: true,
  });

  const candidates = useMemo(
    () => (result?.data ?? []).map(toPipelineCandidate),
    [result],
  );
  const pagination = result?.pagination;
  const vacancyTitles = useMemo(
    () => new Map(vacancies.map((v) => [v.id, v.jobTitle])),
    [vacancies],
  );

  const refresh = () => {
    queryClient.invalidateQueries(['pipeline']);
    queryClient.invalidateQueries(['ats-summary']);
    queryClient.invalidateQueries(['candidates']);
  };

  const onMove = async (candidate: PipelineCandidate, next: PipelineStage) => {
    if (next === candidate.stage) return;
    if (next === 'rejected') {
      setRejecting(candidate);
      return;
    }
    setMovingId(candidate.id);
    try {
      await moveCandidateStage(candidate.id, next);
      toast({
        variant: 'success',
        title: `${candidate.name} moved to ${STAGE_LABEL[next]}.`,
      });
      refresh();
    } catch (error) {
      toast({
        variant: 'error',
        title: errorMessage(error, 'The stage could not be changed.'),
      });
    } finally {
      setMovingId(null);
    }
  };

  const onDelete = async () => {
    if (!deleting) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await deleteCandidate(deleting.id);
      toast({ variant: 'success', title: `${deleting.name} was deleted.` });
      setDeleting(null);
      refresh();
    } catch (error) {
      setDeleteError(errorMessage(error, 'The candidate could not be deleted.'));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="rounded-xl bg-white shadow">
      <div className="flex flex-col gap-4 px-4 py-5 sm:px-6 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Pipeline</h2>
          <p className="text-sm text-gray-600">
            Move candidates through your hiring stages.
          </p>
        </div>
        <button
          type="button"
          className={primaryButtonClass}
          onClick={() => setFormCandidate(null)}
        >
          Add candidate
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3 border-t border-gray-200 px-4 py-4 sm:grid-cols-3 sm:px-6">
        <div>
          <label htmlFor="pipeline-vacancy" className={labelClass}>
            Vacancy
          </label>
          <select
            id="pipeline-vacancy"
            className={fieldClass}
            value={vacancyId}
            onChange={(event) => setVacancyId(event.target.value)}
          >
            <option value="">All vacancies</option>
            {vacancies.map((vacancy) => (
              <option key={vacancy.id} value={vacancy.id}>
                {vacancy.jobTitle}
                {vacancy.status === 'closed' ? ' (closed)' : ''}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="pipeline-stage" className={labelClass}>
            Stage
          </label>
          <select
            id="pipeline-stage"
            className={fieldClass}
            value={stage}
            onChange={(event) => setStage(event.target.value as PipelineStage)}
          >
            <option value="">All stages</option>
            {PIPELINE_STAGES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="pipeline-search" className={labelClass}>
            Search
          </label>
          <input
            id="pipeline-search"
            type="search"
            className={fieldClass}
            placeholder="Name, email or position"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
        </div>
      </div>

      <div className="overflow-x-auto border-t border-gray-200">
        {isLoading ? (
          <p className="px-6 py-10 text-center text-gray-500">
            Loading candidates…
          </p>
        ) : isError ? (
          <p role="alert" className="px-6 py-10 text-center text-red-600">
            Candidates could not be loaded. Please refresh the page.
          </p>
        ) : candidates.length === 0 ? (
          <p className="px-6 py-10 text-center text-gray-500">
            No candidates match these filters.
          </p>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
              <tr>
                <th scope="col" className="px-4 py-3 sm:px-6">
                  Candidate
                </th>
                <th scope="col" className="px-4 py-3">
                  Vacancy
                </th>
                <th scope="col" className="px-4 py-3">
                  Stage
                </th>
                <th scope="col" className="px-4 py-3">
                  Applied
                </th>
                <th scope="col" className="px-4 py-3 text-right sm:px-6">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {candidates.map((candidate) => (
                <tr key={candidate.id}>
                  <td className="px-4 py-3 sm:px-6">
                    <button
                      type="button"
                      className="font-semibold text-gray-900 hover:underline"
                      onClick={() =>
                        navigate(
                          `/ats/candidate/${encodeURIComponent(candidate.id)}`,
                        )
                      }
                    >
                      {candidate.name || 'Unnamed candidate'}
                    </button>
                    <div className="text-gray-500">
                      {candidate.email || 'No email'}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {vacancyTitles.get(candidate.jobId) ||
                      candidate.position ||
                      '—'}
                  </td>
                  <td className="px-4 py-3">
                    <label className="sr-only" htmlFor={`stage-${candidate.id}`}>
                      Stage for {candidate.name}
                    </label>
                    <select
                      id={`stage-${candidate.id}`}
                      className="rounded-md border border-gray-300 px-2 py-1 text-sm"
                      value={candidate.stage}
                      disabled={movingId === candidate.id}
                      onChange={(event) =>
                        onMove(candidate, event.target.value as PipelineStage)
                      }
                    >
                      {PIPELINE_STAGES.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-gray-500">
                    {candidate.dateCreated
                      ? moment(candidate.dateCreated).format('D MMM YYYY')
                      : '—'}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right sm:px-6">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        className="text-sm font-medium text-gray-700 hover:underline"
                        onClick={() => setEmailing(candidate)}
                        aria-label={`Email ${candidate.name}`}
                      >
                        Email
                      </button>
                      <button
                        type="button"
                        className="text-sm font-medium text-gray-700 hover:underline"
                        onClick={() => setFormCandidate(candidate)}
                        aria-label={`Edit ${candidate.name}`}
                      >
                        Edit
                      </button>
                      {candidate.stage !== 'rejected' && (
                        <button
                          type="button"
                          className="text-sm font-medium text-orange-600 hover:underline"
                          onClick={() => setRejecting(candidate)}
                          aria-label={`Reject ${candidate.name}`}
                        >
                          Reject
                        </button>
                      )}
                      <button
                        type="button"
                        className="text-sm font-medium text-red-600 hover:underline"
                        onClick={() => {
                          setDeleteError(null);
                          setDeleting(candidate);
                        }}
                        aria-label={`Delete ${candidate.name}`}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {pagination && pagination.total > 0 && (
        <nav
          aria-label="Pipeline pages"
          className="flex items-center justify-between border-t border-gray-200 px-4 py-3 text-sm sm:px-6"
        >
          <span className="text-gray-600">
            {pagination.total} candidate{pagination.total === 1 ? '' : 's'} ·
            page {pagination.page} of {pagination.totalPages}
            {isFetching ? ' · updating…' : ''}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              className={secondaryButtonClass}
              disabled={pagination.page <= 1}
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
            >
              Previous
            </button>
            <button
              type="button"
              className={secondaryButtonClass}
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </nav>
      )}

      <CandidateFormDialog
        isOpen={formCandidate !== undefined}
        candidate={formCandidate}
        vacancies={vacancies}
        defaultVacancyId={vacancyId}
        onClose={() => setFormCandidate(undefined)}
        onSaved={() => {
          toast({
            variant: 'success',
            title: formCandidate ? 'Candidate saved.' : 'Candidate added.',
          });
          refresh();
        }}
      />

      <RejectCandidateDialog
        candidate={rejecting}
        onClose={() => setRejecting(null)}
        onDone={(emailed) => {
          toast({
            variant: 'success',
            title: emailed
              ? 'Candidate rejected and emailed.'
              : 'Candidate rejected.',
          });
          refresh();
        }}
      />

      <OfferVacancyDialog
        isOpen={Boolean(emailing)}
        candidate={
          emailing
            ? {
                id: emailing.id,
                name: emailing.name,
                email: emailing.email,
                position:
                  vacancyTitles.get(emailing.jobId) || emailing.position,
              }
            : null
        }
        initialTemplateId={
          emailing?.stage === 'applied' || emailing?.stage === 'screening'
            ? 'interview-invite'
            : 'offer'
        }
        onClose={() => setEmailing(null)}
        onSent={refresh}
      />

      <Modal
        isOpen={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        title={`Delete ${deleting?.name ?? 'candidate'}?`}
        description="This deletes the candidate, their stored resume and their interviews. It cannot be undone."
      >
        {deleteError && (
          <p role="alert" className="mb-3 text-sm text-red-600">
            {deleteError}
          </p>
        )}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            className={secondaryButtonClass}
            onClick={() => setDeleting(null)}
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button
            type="button"
            className={primaryButtonClass}
            onClick={onDelete}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting…' : 'Delete candidate'}
          </button>
        </div>
      </Modal>
    </div>
  );
};
